import React, { PureComponent } from "react";

import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  LabelList,
  ReferenceDot,
} from "recharts";

import {
  selectFormat,
  Error,
  CustomizedYAxisTick,
  CustomizedXAxisTickTimeDay,
  StandardTooltip,
  isDateFromDataInISOFormat,
  formatDateFromData,
} from "components/Utils/Utils.js";

import { XRayTooltip } from "components/Utils/XRayTooltip/XRayTooltip.js";

import {
  fetchInsights,
  RenderTooltipInsights,
  OpenSelectedInsight,
  CloseSelectedInsight,
  InsightsWithinTooltip,
  InsightsTickIndicator,
  InsightsLineLabel,
  closeAllLinechartTooltips,
} from "components/Utils/InsightsUtils.js";

import { getXrayTooltipPosition } from "helpers/DOM";

class DailyActivesLive extends PureComponent {
  constructor(props) {
    super(props);
    this.node = React.createRef();
    this.state = {
      widgetsData: props.data.data,
      widgetsInsights: [],
      width: 0,
      height: 0,
      tooltip: {
        show: false,
        x: 0,
        y: 0,
      },
    };
    this.divPosition = React.createRef();
    this.updatePosition = this.updatePosition.bind(this);
  }

  updatePosition() {
    closeAllLinechartTooltips();

    if (!this.chartRef) return;
    const { containerWidth, containerHeight } = this.chartRef.state;
    this.setState({ width: containerWidth, height: containerHeight });
  }

  componentWillMount() {
    this.updatePosition();
  }

  componentDidMount() {
    this.props.childRef(this);

    window.addEventListener("resize", this.updatePosition);
    window.addEventListener("click", this.handleClickOutside);

    setTimeout(() => {
      this.updatePosition();
    }, 500);

    // Fetch insights
    const { data, project_id, project, insights, insightsConfig } = this.props;

    if (insights) {
      const insightsPromise = fetchInsights(
        data,
        project,
        project_id,
        insightsConfig
      );

      if (insightsPromise !== undefined) {
        insightsPromise
          .then((data) => {
            if (data && data.length > 0) {
              this.setState({
                widgetsInsights: data,
              });
            }
          })
          .catch((error) => {
            console.log(error);
          });
      }
    }
  }

  componentWillUnmount() {
    window.removeEventListener("resize", this.updatePosition);
    window.removeEventListener("click", this.handleClickOutside);
  }

  getExportData = () => ({
    data: this.props.data.data.chart,
    opts: {
      type: "chart",
      date: {
        // overwrites the default settings for date only when the date is in ISO format
        defined: isDateFromDataInISOFormat(this.props.data.data.chart),
        format: "YYYY-MM-DDThh:mm:ssZ",
        viewFormat: "YYYY-MM-DD",
      },
    },
  });

  closeTooltip = () => {
    this.setState({
      tooltip: { show: false },
    });
  };

  handleClickOutside = (e) => {
    if (this.state.tooltip.show)
      if (
        this.divPosition.current &&
        this.divPosition.current.contains(e.target)
      ) {
        return;
      } else {
        closeAllLinechartTooltips();
      }
  };

  render() {
    const { data, title } = this.props;
    const { widgetsInsights } = this.state;

    let widget_data = data.data.chart;

    const insightsData = widgetsInsights.length > 0;

    // we map some settings related to label and color to be used in the tooltips
    // since there's no way to get those informations (that doesn't come as properties);
    const tooltipsSettings = {
      ctv_impressions: { label: "CTV Impressions", color: "#FA6E58" },
      display_impressions: { label: "Display Impressions", color: "#FBD14A" },
      mdt_impressions: { label: "MDT Impressions", color: "#2AC8DD" },
      video_impressions: { label: "Video Impressions", color: "#2CDD88" },
    };

    if (data.hasData === false) {
      return <Error minHeight={360} />;
    }

    let preparedData = formatDateFromData(widget_data);

    return (
      <div>
        {/* <div className="row">
          <div className="col-12 mt-2 d-flex align-items-end">
            <div className="col-auto">
              <Heading size={6} className={"mb-1"}>
                Daily Active Users
              </Heading>
              <Metric {...data.daily_active} size={"md"} />
            </div>

            <div className="col-auto">
              <Heading size={6} className={"mb-1"}>
                Weekly Active Users
              </Heading>
              <Metric {...data.weekly_active} size={"md"} />
            </div>

            <div className="col-auto">
              <Heading size={6} className={"mb-1"}>
                Monthly Active Users
              </Heading>
              <Metric {...data.monthly_active} size={"md"} />
            </div>
          </div>
        </div> */}

        <div className="d-flex mt-3" ref={this.divPosition}>
          <ResponsiveContainer height={340} width="100%">
            <AreaChart
              width={600}
              height={400}
              data={preparedData}
              margin={{ top: 12, right: 30, left: 0, bottom: 20 }}
              onMouseOver={(e) => CloseSelectedInsight(this.node)}
              onMouseOut={(e) =>
                OpenSelectedInsight(this.node, !this.state.tooltip.show)
              }
              isAnimationActive={false}
              onClick={(d) => {
                const extra = {
                  ctv_impressions: {
                    label: "CTV Impressions",
                    color: "#FA6E58",
                  },
                  display_impressions: {
                    label: "Display Impressions",
                    color: "#FBD14A",
                  },
                  mdt_impressions: {
                    label: "MDT Impressions",
                    color: "#2AC8DD",
                  },
                  video_impressions: {
                    label: "Video Impressions",
                    color: "#2CDD88",
                  },
                };

                if (d !== null) {
                  d.extra = extra;
                  this.setState({
                    tooltip: { ...d, show: true },
                  });
                } else {
                  this.closeTooltip();
                }
              }}
            >
              <CartesianGrid
                stroke="#FEE5DE"
                strokeDasharray="3 3"
                vertical={false}
              />

              <Area
                type="monotone"
                dataKey="display_impressions"
                // stroke="#FCE39C"
                stroke="none"
                fill="url(#display_impressions)"
                isAnimationActive={false}
              />

              <Area
                type="monotone"
                dataKey="ctv_impressions"
                // stroke="#FAC1C0"
                stroke="none"
                fill="url(#ctv_impressions)"
                isAnimationActive={false}
              />

              <Area
                type="monotone"
                dataKey="mdt_impressions"
                // stroke="#AAE8F0"
                stroke="none"
                fill="url(#mdt_impressions)"
                isAnimationActive={false}
              />

              <Area
                type="monotone"
                dataKey="video_impressions"
                // stroke="#A1EEC8"
                stroke="none"
                fill="url(#video_impressions)"
                isAnimationActive={false}
              >
                {this.state.widgetsInsights.length > 0 && (
                  <LabelList
                    dataKey="date"
                    content={
                      <InsightsLineLabel
                        height={290}
                        data={preparedData}
                        insights={this.state.widgetsInsights}
                        showIndicator={!this.state.tooltip.show}
                        node={this.node}
                      />
                    }
                  />
                )}
              </Area>

              <defs>
                <linearGradient
                  id="ctv_impressions"
                  x1="0%"
                  y1="0%"
                  x2="0%"
                  y2="100%"
                >
                  <stop offset="5%" stopColor="#FA6E58" stopOpacity={1} />
                  <stop offset="95%" stopColor="#FA6E58" stopOpacity={0.1} />
                </linearGradient>

                <linearGradient
                  id="display_impressions"
                  x1="0%"
                  y1="0%"
                  x2="0%"
                  y2="100%"
                >
                  <stop offset="5%" stopColor="#FBD14A" stopOpacity={1} />
                  <stop offset="95%" stopColor="#FBD14A" stopOpacity={0.1} />
                </linearGradient>

                <linearGradient
                  id="mdt_impressions"
                  x1="0%"
                  y1="0%"
                  x2="0%"
                  y2="100%"
                >
                  <stop offset="5%" stopColor="#2AC8DD" stopOpacity={1} />
                  <stop offset="95%" stopColor="#2AC8DD" stopOpacity={0.1} />
                </linearGradient>

                <linearGradient
                  id="video_impressions"
                  x1="0%"
                  y1="0%"
                  x2="0%"
                  y2="100%"
                >
                  <stop offset="5%" stopColor="#2CDD88" stopOpacity={1} />
                  <stop offset="95%" stopColor="#2CDD88" stopOpacity={0.1} />
                </linearGradient>
              </defs>

              <XAxis
                dataKey="date"
                axisLine={false}
                tickLine={false}
                scale="auto"
                tick={<CustomizedXAxisTickTimeDay />}
              />

              <YAxis
                axisLine={false}
                tickLine={false}
                mirror={true}
                tick={<CustomizedYAxisTick />}
              />

              {/* Adds insight icon under x-axis ticks  */}
              {insightsData &&
                widgetsInsights.map((o, index) => (
                  <ReferenceDot
                    x={o.date}
                    y={0}
                    key={title + index}
                    ifOverflow="extendDomain"
                    shape={
                      <InsightsTickIndicator
                        insights={widgetsInsights}
                        date={o.date}
                        node={this.node}
                      />
                    }
                  />
                ))}

              {!this.state.tooltip.show && (
                <Tooltip
                  cursor={{ fill: "#eee", opacity: 0.5 }}
                  content={
                    <StandardTooltip
                      render={({ data }) => {
                        const payload = data(this.props);
                        if (payload !== null) {
                          const tooltip_data = payload[0].payload;

                          return (
                            <div className="standard-tooltip">
                              {tooltip_data.date && (
                                <header className="standard-tooltip__header">
                                  <span className="standard-tooltip__key-header">
                                    Date:
                                  </span>
                                  <span className="standard-tooltip__value-header">
                                    {tooltip_data.date}
                                  </span>
                                </header>
                              )}
                              <main className="standard-tooltip__main">
                                {Object.keys(payload)
                                  .sort(
                                    (a, b) =>
                                      payload[b].value - payload[a].value
                                  )
                                  .filter((key) => payload[key].value > 0)
                                  .map((key, index) => {
                                    return (
                                      <div
                                        className="standard-tooltip__item"
                                        key={key}
                                      >
                                        <span
                                          className="standard-tooltip__icon"
                                          // set the backgroun color according the chart name
                                          style={{
                                            background:
                                              tooltipsSettings[
                                                payload[key].name
                                              ].color,
                                          }}
                                        />
                                        <span
                                          className="standard-tooltip__key"
                                          style={{
                                            color: payload[key].color,
                                            textTransform: "capitalize",
                                          }}
                                        >
                                          {/* set the label according the chart name */}
                                          {
                                            tooltipsSettings[payload[key].name]
                                              .label
                                          }
                                        </span>
                                        <span className="standard-tooltip__value">
                                          {selectFormat(
                                            "number",
                                            payload[key].value
                                          )}
                                        </span>
                                      </div>
                                    );
                                  })}

                                {this.state.widgetsInsights && (
                                  <InsightsWithinTooltip
                                    data={this.state.widgetsInsights}
                                    tooltipDate={tooltip_data.date}
                                  />
                                )}
                              </main>
                            </div>
                          );
                        }
                      }}
                    />
                  }
                />
              )}
            </AreaChart>
          </ResponsiveContainer>

          {this.state.tooltip.activeCoordinate ? (
            <div
              className="xray-tooltip-container"
              style={{
                top: this.state.tooltip.activeCoordinate.y + 70 + "px",
                left:
                  this.state.tooltip.activeCoordinate.x + 1062 + 220 <
                  this.state.width
                    ? this.state.tooltip.activeCoordinate.x + 15 + "px"
                    : this.state.tooltip.activeCoordinate.x -
                      getXrayTooltipPosition(
                        this.divPosition.current,
                        this.state.tooltip.activeCoordinate
                      ) +
                      "px",
                opacity: this.state.tooltip.show ? 1.0 : 0.0,
                visibility: this.state.tooltip.show ? "visible" : "hidden",
              }}
            >
              <XRayTooltip
                data={this.state.tooltip}
                insights={widgetsInsights}
                close={this.closeTooltip.bind(this)}
              />
            </div>
          ) : null}
          <div className="featured-insight-tooltip" ref={this.node}>
            {widgetsInsights.length > 0 && !this.state.tooltip.show && (
              <RenderTooltipInsights data={this.state.widgetsInsights} />
            )}
          </div>
        </div>
      </div>
    );
  }
}

export default DailyActivesLive;
